// Pure helpers behind the gauge detail panel, ported from GaugeChartViewModel
// (GaugeChartView.swift) and GaugeDetailViewModel.swift. No React or DOM here
// so the tests run under node.
import { GaugeNotFoundError, validStage } from '../../api/nwps';
import { USGSNoDataError } from '../../api/usgs';
import { HttpError, isAbortError } from '../../lib/fetchJson';
import { formatDateTime, formatNumber, parseISODate } from '../../lib/format';
import type { NWPSFloodCategories, StageFlowPoint, StageFlowResponse } from '../../types/gauges';
import type { USGSSeriesPoint } from '../../types/usgs';

// MARK: - Stage/flow readings

export type SeriesKind = 'observed' | 'forecast';

export interface StageReading {
  time: Date;
  stage: number;
  series: SeriesKind;
}

/** ChartTimeRange: how much of the hydrograph to plot. */
export type ChartTimeRange = 'recent' | 'all';

export const TIME_RANGE_OPTIONS: { value: ChartTimeRange; label: string }[] = [
  { value: 'recent', label: '±72 Hours' },
  { value: 'all', label: 'All Data' },
];

/** `.recent` keeps the last 72 h of observations and the next 72 h of forecast. */
export const RECENT_WINDOW_MS = 72 * 60 * 60 * 1000;

export interface StageFlowModel {
  readings: StageReading[];
  unit: string;
}

/**
 * GaugeChartViewModel.load: observed and forecast points flattened into one
 * time-sorted list. Negative stages are NWPS "missing" sentinels and are
 * dropped (`.filter { $0.stage >= 0 }`); the unit comes from the observed series.
 */
export function buildStageFlowModel(response: StageFlowResponse | null | undefined, defaultUnit = 'ft'): StageFlowModel {
  const readings: StageReading[] = [];
  const add = (series: SeriesKind, data: StageFlowPoint[] | null | undefined) => {
    for (const p of data ?? []) {
      const time = parseISODate(p.validTime);
      if (!time || typeof p.primary !== 'number' || !Number.isFinite(p.primary) || p.primary < 0) continue;
      readings.push({ time, stage: p.primary, series });
    }
  };
  add('observed', response?.observed?.data);
  add('forecast', response?.forecast?.data);
  readings.sort((a, b) => a.time.getTime() - b.time.getTime());
  return { readings, unit: response?.observed?.primaryUnits || defaultUnit };
}

/** GaugeChartViewModel.visibleReadings: everything, or ±72 h around `now`. */
export function visibleReadings(readings: StageReading[], range: ChartTimeRange, now: Date): StageReading[] {
  if (range !== 'recent') return readings;
  const start = now.getTime() - RECENT_WINDOW_MS;
  const end = now.getTime() + RECENT_WINDOW_MS;
  return readings.filter((r) => {
    const t = r.time.getTime();
    return t >= start && t <= end;
  });
}

export function observedSeries(readings: StageReading[]): StageReading[] {
  return readings.filter((r) => r.series === 'observed');
}

export function forecastSeries(readings: StageReading[]): StageReading[] {
  return readings.filter((r) => r.series === 'forecast');
}

/** Forecast line bridged to the last observed point so the two lines meet (GaugeChartViewModel.forecastLine). */
export function forecastLine(visible: StageReading[]): StageReading[] {
  const fc = forecastSeries(visible);
  const lastObs = observedSeries(visible).at(-1);
  if (!lastObs) return fc;
  return [{ time: lastObs.time, stage: lastObs.stage, series: 'forecast' }, ...fc];
}

// MARK: - Flood thresholds

export type ThresholdKey = 'action' | 'minor' | 'moderate' | 'major';

export interface Threshold {
  key: ThresholdKey;
  name: string;
  value: number;
}

const THRESHOLD_ORDER: ReadonlyArray<{ key: ThresholdKey; name: string }> = [
  { key: 'action', name: 'Action' },
  { key: 'minor', name: 'Minor' },
  { key: 'moderate', name: 'Moderate' },
  { key: 'major', name: 'Major' },
];

/** Reference lines for the stage chart. NWPS marks an undefined threshold with -9999, which validStage rejects. */
export function buildThresholds(categories: NWPSFloodCategories | null | undefined): Threshold[] {
  if (!categories) return [];
  const out: Threshold[] = [];
  for (const { key, name } of THRESHOLD_ORDER) {
    const stage = categories[key]?.stage;
    if (validStage(stage)) out.push({ key, name, value: stage });
  }
  return out;
}

// MARK: - Units

export const SQ_MI_PER_SQ_KM = 0.386102;

export function kmToSqMi(km: number): number {
  return km * SQ_MI_PER_SQ_KM;
}

/** GaugeDetailView.metadataCard: "%.2f mi² (%.2f km²)". */
export function formatDrainageArea(km: number): string {
  return `${kmToSqMi(km).toFixed(2)} mi² (${km.toFixed(2)} km²)`;
}

// MARK: - Identifiers and links

/** USGS site numbers are 8–15 digits; NWS location ids (e.g. HARP1) are not. */
export function isUsgsSiteNumber(id: string): boolean {
  return /^\d{8,15}$/.test(id.trim());
}

/**
 * Site number for the USGS calls: NWPS metadata's usgsId when it has one,
 * otherwise the gauge id itself when it already is a USGS site number.
 */
export function usgsSiteNumber(nwpsUsgsId: string | null | undefined, gaugeId: string): string | null {
  const fromNwps = nwpsUsgsId?.trim();
  if (fromNwps) return fromNwps;
  return isUsgsSiteNumber(gaugeId) ? gaugeId.trim() : null;
}

export interface SourceLink {
  label: string;
  url: string;
}

/** GaugeChartViewModel.sourceURL plus the button label from GaugeChartView.sourceButton. */
export function sourceLink(usgsId: string | null, nwpsId: string): SourceLink {
  if (usgsId) {
    return { label: 'View Source on USGS', url: `https://waterdata.usgs.gov/monitoring-location/USGS-${encodeURIComponent(usgsId)}/` };
  }
  return { label: 'View Source on NOAA', url: `https://water.noaa.gov/gauges/${encodeURIComponent(nwpsId)}` };
}

// MARK: - Errors

export function isNotFoundError(err: unknown): boolean {
  return err instanceof GaugeNotFoundError || (err instanceof HttpError && err.status === 404);
}

/** One readable sentence for a failed NWPS or USGS request. */
export function describeError(err: unknown, service: 'NWPS' | 'USGS', id: string): string {
  if (err instanceof GaugeNotFoundError) return `NWPS has no gauge record for ${id}, so stage data is unavailable.`;
  if (err instanceof USGSNoDataError) return err.message;
  if (err instanceof HttpError) return err.status === 404 ? `${service} has no data for ${id}.` : `${service} returned HTTP ${err.status}.`;
  if (isAbortError(err)) return `The ${service} request timed out.`;
  if (err instanceof Error && err.message) return err.message;
  return `The ${service} request failed.`;
}

// MARK: - Accessible descriptions

export function describeStageChart(visible: StageReading[], thresholds: Threshold[], unit: string): string {
  if (visible.length === 0) return 'Stage chart with no readings.';
  const observed = observedSeries(visible);
  const forecast = forecastSeries(visible);
  const first = visible[0];
  const last = visible[visible.length - 1];
  const parts = [
    `Stage chart in ${unit}: ${observed.length} observed and ${forecast.length} forecast readings from ${formatDateTime(first.time)} to ${formatDateTime(last.time)}.`,
  ];
  const latest = observed.at(-1);
  if (latest) parts.push(`Latest observed ${formatNumber(latest.stage, 2)} ${unit} at ${formatDateTime(latest.time)}.`);
  if (forecast.length) {
    const peak = forecast.reduce((m, r) => (r.stage > m.stage ? r : m));
    parts.push(`Forecast peak ${formatNumber(peak.stage, 2)} ${unit} at ${formatDateTime(peak.time)}.`);
  }
  if (thresholds.length) parts.push(`Thresholds: ${thresholds.map((t) => `${t.name} ${formatNumber(t.value, 1)}`).join(', ')} ${unit}.`);
  return parts.join(' ');
}

export function describeSeriesChart(name: string, points: USGSSeriesPoint[], unit: string, digits: number): string {
  if (points.length === 0) return `${name} chart with no readings.`;
  const first = points[0];
  const last = points[points.length - 1];
  let lo = first;
  let hi = first;
  for (const p of points) {
    if (p.value < lo.value) lo = p;
    if (p.value > hi.value) hi = p;
  }
  return (
    `${name} chart: ${points.length} readings from ${formatDateTime(first.date)} to ${formatDateTime(last.date)}. ` +
    `Latest ${formatNumber(last.value, digits)} ${unit}. Range ${formatNumber(lo.value, digits)} to ${formatNumber(hi.value, digits)} ${unit}.`
  );
}
